/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
 */

import { __ } from '@oat-sa-private/ui-core';
import { user } from '@/stores/auth.store.js';
import { configurationStore } from '@/stores/tenantConfiguration.store.js';

export const DEFAULT_LOCALE = 'en-US';
export const DEFAULT_SUPPORTED_LOCALES = { 'en-US': __('English (USA)') };

export const locale = {
    setDictionaryLoader() {
        __.setDictionaryLoader(async localeCode => {
            try {
                const normalizedLocaleCode = normalizeLocaleCode(localeCode);
                const { default: translation } = await import(`../../locale/${normalizedLocaleCode}/messages.json`);
                return translation;
            } catch (err) {
                return {};
            }
        });

        /**
         * Normalize the locale codes.
         * Case sensitive and insensitive locale codes both are normalized to the format used in the locale folders.
         * For example, 'en-us' or 'en-US' will be both normalized to 'en-US'.
         * Exceptions, like ca-ES-valencia or en-GB-x-excscotl are also properly handled, as first part will be lowercased and second uppercased.
         */
        function normalizeLocaleCode(localeCode) {
            const [lang, region, ...rest] = localeCode.split('-');
            return [lang.toLowerCase(), region?.toUpperCase(), ...rest].filter(Boolean).join('-');
        }
    },

    /**
     * Set the application locale.
     *
     * @param {String} langCode - unified language key.
     *
     * @returns {Promise} - rejected if code is not supported, resolved otherwise.
     */
    setApplicationLocale(langCode) {
        return __.setLocale(langCode);
    },

    /**
     * Sets the lang attribute on html tag
     *
     * @param {String} langCode - unified language key.
     */
    setLangAttribute(langCode) {
        document.documentElement.setAttribute('lang', langCode);
    },

    /**
     * Sets the locale for the application based on priority
     * @param {string} [requestedLocale]
     * @returns {Promise<void>}
     */
    async updateApplicationLocale(requestedLocale) {
        const { language } = user.get() || {};
        const { locale: defaultLocale } = configurationStore.get() || {};

        let platformLanguage = defaultLocale;

        if (requestedLocale) {
            platformLanguage = requestedLocale;
        }

        if (language) {
            platformLanguage = language;
        }

        await this.setApplicationLocale(platformLanguage);
        this.setLangAttribute(platformLanguage);
    }
};
